import { faPaperPlane } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { ToastContext } from "../../../context/ToastContext";
import _ from "underscore";
import services from "../../../services/services";
import { Urls } from "../../../services/Urls";
import { encryptData } from "../../../utils/encrypt";
import * as XLSX from 'xlsx';
import projectStore from "../../../Store/projectStore";
import { calculateWordsFromString, getPreviousMonths } from "../../../utils/Helper";

export const usePrecisionReportEntry = ({ precisionReport, filterValues }) => {

    const { showInfo, showSuccess, showError } = useContext(ToastContext);
    const { options } = projectStore();
    const [selectedRow, setSelectedRow] = useState([]);
    const [data, setData] = useState({});
    const [confirmationPopup, setConfirmationPopup] = useState(false);
    const [search, setSearch] = useState("");
    const [loader, setLoader] = useState(false);
    const [isHide, setIsHide] = useState(false);
    const [popupFilter, setPopupFilter] = useState(filterValues || {});
    const listRef = useRef(null)

    useEffect(() => {
        popupFilter?.apiCall && getPrecisionReport()
    }, [popupFilter?.apiCall])

    useEffect(() => {
        setIsHide((popupFilter?.months?.length !== 1 || ((popupFilter?.precision_lead?.user_code ? popupFilter?.precision_lead?.user_code : precisionReport?.group_key) !== options?.userInfo?.user_code)))
    }, [popupFilter?.months, precisionReport?.group_key])

    const handleKeyPress = (e) => {
        const charCode = e.charCode || e.keyCode;
        if (charCode < 48 || charCode > 57) {
            e.preventDefault();
        }
    };

    const getRatingStyle = (rating) => {
        let styles = { bg: "#cecece", text: "#000000" };
        if (rating <= 0) {
            styles = { bg: "#e7e7e7", text: "#000000" };
        } else if (rating < 4.5) {
            styles = { bg: "#fee2e2", text: "#991b1b" };
        } else if (rating < 6.0) {
            styles = { bg: "#ffedd5b9", text: "#91471c" };
        } else if (rating < 7.5) {
            styles = { bg: "#fef3c7", text: "#78350f" };
            // } else if (rating < 8.9) {
            //     styles = { bg: "#e3ffed", text: "#166534" };
            // } else if (rating <= 10) {
            //     styles = { bg: "#b6f2cb", text: "#0e5027" };
            // }
        } else if (rating < 8.9) {
            styles = { bg: "#b6f2cb", text: "#0e5027" };
        } else if (rating <= 10) {
            styles = { bg: "#047857", text: "#ffff" };
        }
        return {
            backgroundColor: styles.bg,
            color: styles.text,
            textAlign: "center",
            fontWeight: "700",
        };
    };

    const rankKeys = [
        { id: "bu_rank", label: "Business Understanding", prev: "prev_bu_rank" },
        { id: "db_eff_rank", label: "Database Efficiency", prev: "prev_db_eff_rank" },
        { id: "tech_rank", label: "Technical Efficiency", prev: "prev_tech_rank" },
        { id: "time_efficiency_rank", label: "Time Effort", prev: "prev_time_efficiency_rank" },
        { id: "extra_efforts", label: "Extra Efforts", prev: "prev_extra_efforts" },
        { id: "overall_standing_rank", label: "Overall", prev: "prev_overall_standing_rank" },
    ];

    const columns = useMemo(() => {
        const baseColumns = [
            {
                key: "selection",
                label: "",
                checkbox: true,
                style: { width: "2%" },
                headerStyle: { textAlign: "center" },
                sort: false,
                hide: isHide,
                render: (row, isAll) => {
                    const submitData = data?.rowData?.filter(item => !item?.is_submit && item?.is_save) || [];
                    const isChecked = isAll
                        ? (submitData.length > 0 && selectedRow?.length === submitData.length)
                        : selectedRow?.some(item => item.user_code === row.user_code);

                    return (
                        <div className="d-flex justify-content-center w100">
                            {!row?.is_submit && (
                                <input
                                    type="checkbox"
                                    checked={isChecked}
                                    className="input-dd"
                                    disabled={(!row?.is_save && !isAll) || row?.isDisable}
                                    onChange={() => handleChange(row, isAll, submitData)}
                                />
                            )}
                        </div>
                    );
                },
            },
            {
                key: "user_display_name",
                label: "Member",
                style: { width: "15%", fontWeight: "bold" },
                render: (row) => (
                    <div className="d-flex gap-2 align-items-center">
                        <img src={row?.image_url}
                            alt=""
                            width={25}
                            height={25}
                            className=" rounded-circle"
                            onError={(e) => { e.target.onerror = null; e.target.src = "/assets/grey-user-icon.png"; }} />
                        {row.user_display_name}
                    </div>
                )
            },
            {
                key: "project_role_name",
                label: "Role",
                style: { width: "13%" },
                render: (row) => (
                    <div className="fw-semibold text-capitalize" style={{ color: "#838e9e", fontSize: "13px" }}>
                        {row.project_role_name}
                    </div>
                )
            },
        ];

        const dynamicRankCols = rankKeys.map(rank => ({
            key: rank.id,
            label: rank.label,
            headerStyle: { textAlign: "center" },
            style: { width: "6%" },
            render: (row) => (
                <div className="w100 d-flex justify-content-center position-relative"
                    title={row[rank.prev] ? `prev: ${row[rank.prev]}` : ""}>
                    {row[rank.prev] ? (
                        <div className="position-absolute" style={{ right: "0px", top: "-10px", color: "#aab1bb", fontSize: "11px" }}>
                            {row[rank.prev]}
                        </div>
                    ) : ""}
                    <input
                        type="text"
                        className="text-center w75 py-2 rounded"
                        onKeyPress={handleKeyPress}
                        style={getRatingStyle(parseFloat(row[rank.id]))}
                        value={row[rank.id] || ""}
                        name={rank.id}
                        onChange={(e) => handleEdit(e, row.user_code)}
                        disabled={row?.isDisable}
                        onBlur={() => row?.isChange && savePrecisionEntry(row)}
                    />
                </div>
            )
        }))

        const endColumns = [
            {
                key: "remark",
                label: "Remarks",
                style: { width: "23%", flex: 1 },
                render: (row) => (
                    <div className='p-2'>
                        <input
                            type="text"
                            className="sk-input-dd w100"
                            value={row?.remark || ""}
                            name="remark"
                            onChange={(e) => handleEdit(e, row?.user_code)}
                            onBlur={() => row?.isChange && savePrecisionEntry(row)}
                            title={row?.remark || ""}
                            disabled={row?.isDisable}
                        />
                    </div>
                )
            },
            {
                key: "submission_status",
                label: "Status",
                headerStyle: { textAlign: "center" },
                style: { width: "10%" },
                hide: isHide,
                render: (row) => (
                    <div className="d-flex gap-1 justify-content-center align-items-center px-1">
                        {row?.is_submit ? (
                            <div className="d-flex justify-content-center w100">
                                <div className="fw-bold px-1 rounded" style={{ fontSize: "10px", background: "#e3ffed", color: "#166534", border: "1px solid #b6f2cb" }}>
                                    Submitted
                                </div>
                            </div>
                        ) : (
                            row?.due_days ? <small className="text-danger fw-semibold px-2">Due: {row?.due_days} Days</small> : null
                        )}
                        {!row?.is_submit && row?.manager_user_code === options?.userInfo?.user_code && !row?.isDisable && (
                            <div className="d-flex justify-content-center">
                                <FontAwesomeIcon
                                    icon={faPaperPlane}
                                    className="text-success fw-semibold c-pointer"
                                    style={{ fontSize: "15px" }}
                                    onClick={() => validateReport([row])}
                                />
                            </div>
                        )}
                    </div>
                )
            },
        ];
        return [...baseColumns, ...dynamicRankCols, ...endColumns];

    }, [data?.rowData, selectedRow, isHide]);


    const handleChange = (row, isAll, submitData) => {
        if (isAll) {
            const isAllSelected = submitData.length > 0 && selectedRow?.length === submitData?.length;
            setSelectedRow(isAllSelected ? [] : [...submitData]);
        } else {
            setSelectedRow(prev => {
                const isSelected = _.some(prev, (item) => item?.user_code === row?.user_code);
                if (isSelected) {
                    return _.filter(prev, (item) => item?.user_code !== row?.user_code);
                } else {
                    return [...prev, row];
                }
            });
        }
    };

    const handleEdit = (e, userCode) => {
        const { name, value, type } = e.target;
        let cleaned = value?.replace(/[^0-9]/g, '');
        const requiredFields = [
            "bu_rank",
            "tech_rank",
            "time_efficiency_rank",
            "overall_standing_rank",
        ];
        if (cleaned.startsWith('0')) {
            showInfo("warn", "Value cannot be 0 or start with 0");
            return;
        }
        if (parseInt(cleaned, 10) > 10 && requiredFields?.includes(name)) {
            showInfo("warn", "Value cannot be greater than 10");
            return;
        }

        const finalValue = type === "number" ? cleaned : value;
        setData(prevData => ({
            ...prevData,
            rowData: prevData?.rowData?.map(item =>
                item.user_code === userCode
                    ? { ...item, [name]: finalValue, isChange: true }
                    : item
            )
        }));

        if (!_.isEmpty(selectedRow)) setSelectedRow(prev => [...prev?.map(item => item?.user_code === userCode ? { ...item, [name]: finalValue } : item)]);
    };

    const savePrecisionEntry = async (row) => {
        try {

            const payload = {
                "precision_pulse_metric_id": row?.precision_pulse_metric_id || 0,
                "month": row?.report_month,
                "year": row?.report_year,
                "Requested_user_code": row?.user_code,
                "bu_rank": encryptData(row?.bu_rank || "0"),
                "db_eff_rank": encryptData(row?.db_eff_rank || "0"),
                "tech_rank": encryptData(row?.tech_rank || "0"),
                "time_efficiency_rank": encryptData(row?.time_efficiency_rank || "0"),
                "overall_standing_rank": encryptData(row?.overall_standing_rank || "0"),
                "extra_efforts": encryptData(row?.extra_efforts || "0"),
                "remark": encryptData(row?.remark),
                "is_save": true
            }
            const res = await services.getInvokeRemote("POST", Urls("UPSERTTEAMOVERVIEWPRECISIONPULSEMETRICS"), payload);
            if (!res?.body?.error_status) {
                // showSuccess("Success", res?.body?.message);
                setData(prevData => ({
                    ...prevData,
                    rowData: prevData?.rowData?.map(item =>
                        item.user_code === row?.user_code
                            ? { ...row, isChange: false, is_save: true }
                            : item
                    )
                }));
            } else {
                showError('Error', res?.body.message);
            }

        } catch (error) {
            showError("error", error?.response?.data?.errorMessage);
        }
    }

    const validateReport = (reportData) => {
        const savedEmp = reportData.filter(emp => emp.is_save);
        if (savedEmp?.length === 0) {
            showInfo("warn", "Please select at least one employee report");
        } else {
            for (const report of reportData) {
                const requiredFields = [
                    "bu_rank",
                    "tech_rank",
                    "time_efficiency_rank",
                    "overall_standing_rank",
                    "remark"
                ];

                const isMissingField = requiredFields.some(field =>
                    !report[field] || report[field].toString().trim() === ""
                );

                if (isMissingField) {
                    showInfo("warn", "Please fill all required ranking fields and remarks");
                    setConfirmationPopup(false);
                    return false;
                }
                if (calculateWordsFromString(report.remark) < 5) {
                    showInfo("warn", "Remark should be at least 5 words long");
                    setConfirmationPopup(false);
                    return false;
                }
            }
            handleSubmit(reportData);
        }
    };

    const getPrecisionReport = async (scrollTop) => {
        try {
            setLoader(true);
            const monthYearRange = popupFilter?.months?.map(item => `${item?.month?.toString()?.padStart(2, "0")}/${item?.year}`) || [];
            const isProjectLead = (precisionReport?.fromRankDist || (popupFilter?.precision_lead?.user_code && precisionReport?.group_key)) && !popupFilter?.applyInnerFilters;
            setData({ rowData: [], tempData: [] });
            const isMonth = precisionReport?.group_key?.toString()?.includes("/");
            const payload = {
                "users": _.pluck(popupFilter?.users, "user_code")?.join(",") || "",
                "project_ids": _.pluck(popupFilter?.projects, "project_id")?.join(",") || "",
                "project_role_ids": _.pluck(popupFilter?.project_role, "project_role_id")?.join(",") || "",
                "month_year_range": (precisionReport?.fromRankDist || isMonth) ? precisionReport?.group_key : monthYearRange.join(",") || "",
                "project_lead_user_codes": (!popupFilter?.applyInnerFilters ? (filterValues?.precision_lead?.user_code?.toString() || (isMonth ? "" : precisionReport?.group_key?.toString())) : popupFilter?.precision_lead?.user_code?.toString()) || ((!precisionReport?.fromRankDist && !isProjectLead) ? precisionReport?.group_key?.toString() : "") || "",
                // "month_year_range": isProjectLead ? String(precisionReport?.group_key) : monthYearRange.join(",") || "",
                // "project_lead_user_codes": (!popupFilter?.applyInnerFilters ? filterValues?.precision_lead?.user_code?.toString() : popupFilter?.precision_lead?.user_code?.toString()) || ((!precisionReport?.fromRankDist && !isProjectLead) ? precisionReport?.group_key?.toString() : "") || "",
                "rate_range": precisionReport?.range || ""
            };
            let selectedMonths = [];
            payload?.month_year_range?.split(",")?.forEach(item => {
                const opts = getPreviousMonths(12);
                const findData = _.findWhere(opts, { month: parseInt(item?.split("/")?.[0]), year: parseInt(item?.split("/")?.[1]) });
                if (findData) selectedMonths.push(findData);
            })
            if (!popupFilter?.applyInnerFilters) {
                setPopupFilter((prev) => ({ ...prev, months: selectedMonths, applyInnerFilters: true, precision_lead: filterValues?.precision_lead || _.findWhere(options?.precision_lead, { user_code: parseInt(payload?.project_lead_user_codes) }) || null }));
            }
            const res = await services.getInvokeRemote("POST", Urls("GETTEAMOVERVIEWPRECISIONPULSEMETRICS"), payload);
            const rawData = res?.body?.data?.timesheet_data;
            if (!res?.body?.data?.error_status && rawData?.length > 0) {
                let processedData = [];
                const isSingleMonth = (popupFilter?.applyInnerFilters ? popupFilter?.months?.length === 1 : filterValues?.months?.length === 1) || isProjectLead;

                if (isSingleMonth) {
                    const dateSource = isProjectLead
                        ? { month: payload?.month_year_range?.split("/")?.[0], year: payload?.month_year_range?.split("/")?.[1] }
                        // ? { month: precisionReport?.group_key?.split("/")?.[0], year: precisionReport?.group_key?.split("/")?.[1] }
                        : (popupFilter?.applyInnerFilters ? popupFilter?.months?.[0] : filterValues?.months?.[0]);

                    const selectedMonth = parseInt(dateSource.month);
                    const selectedYear = parseInt(dateSource.year);
                    const pMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
                    const pYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
                    const dataMap = new Map();
                    rawData.forEach(item => {
                        const key = `${item.user_code}_${item.report_month}_${item.report_year}`;
                        dataMap.set(key, item);
                    });

                    processedData = rawData
                        .filter(item => item.report_month === selectedMonth && item.report_year === selectedYear)
                        .map(current => {
                            const prevKey = `${current.user_code}_${pMonth}_${pYear}`;
                            const previous = dataMap.get(prevKey);

                            return {
                                ...current,
                                prev_bu_rank: previous?.bu_rank ?? 0,
                                prev_db_eff_rank: previous?.db_eff_rank ?? 0,
                                prev_tech_rank: previous?.tech_rank ?? 0,
                                prev_time_efficiency_rank: previous?.time_efficiency_rank ?? 0,
                                prev_overall_standing_rank: previous?.overall_standing_rank ?? 0,
                                prev_extra_efforts: previous?.extra_efforts ?? 0,
                                isDisable: (monthYearRange.length > 1) ||
                                    (current.manager_user_code !== options?.userInfo?.user_code) ||
                                    current.is_submit
                            };
                        });
                } else {
                    processedData = rawData.map(item => ({
                        ...item,
                        isDisable: (monthYearRange.length > 1) || (item.manager_user_code !== options?.userInfo?.user_code) || item.is_submit
                    }));
                }
                processedData.sort((a, b) => {
                    const aStatus = a.is_submit ? 2 : (a.is_save ? 1 : 0);
                    const bStatus = b.is_submit ? 2 : (b.is_save ? 1 : 0);
                    return (aStatus - bStatus) || a.user_display_name?.localeCompare(b.user_display_name);
                });

                setData({ rowData: processedData, tempData: [...processedData] });
                requestAnimationFrame(() => {
                    if (listRef?.current) {
                        listRef.current.scrollTop = scrollTop || 0;
                    }
                });

            } else {
                setData({ rowData: [], tempData: [] });
            }
        } catch (error) {
            console.error("Precision Report Error:", error);
        } finally {
            setPopupFilter((prev) => ({ ...prev, apiCall: false }));
            setLoader(false)
        }
    };

    const handleSubmit = async (reportData) => {
        try {
            const savedEmp = reportData.filter(emp => emp.is_save);
            if (savedEmp?.length > 0) {
                const payload = {
                    "is_submit": true,
                    "approver_user_code": options?.userInfo?.user_code,
                    "report_month": savedEmp?.[0]?.report_month,
                    "report_year": savedEmp?.[0]?.report_year,
                    "users": _.uniq(_.pluck(savedEmp, "user_code")).toString()
                }
                const res = await services?.getInvokeRemote("POST", Urls("TEAMOVERVIEWPRECISIONPULSEMETRICSSUBMIT"), payload)
                if (!res?.body?.success) {
                    showSuccess("success", res?.body?.message)
                    const savedScroll = listRef.current?.scrollTop
                    getPrecisionReport(savedScroll);
                    setConfirmationPopup(false);
                    setSelectedRow([]);
                } else {
                    showError("Error", res.body.message);
                }
            } else {
                showInfo("warn", "Please select at least one employee report");
            }

        } catch (error) {
            setConfirmationPopup(false);
            console.error(error)
        }
    };

    const precisionDetailsExportToExcel = () => {
        const rawData = data?.rowData?.map(row => ({
            "Member": row.user_display_name || '-',
            "Business Understanding": row.bu_rank || '-',
            "Database Efficiency": row.db_eff_rank || '-',
            "Technical Efficiency": row.tech_rank || '-',
            "Time Effort": row.time_efficiency_rank || '-',
            "Extra Efforts": row.extra_efforts || '-',
            "Overall": row.overall_standing_rank || '-',
            "Remark": row.remark
        }));
        const objectMaxLength = [];
        const worksheetData = [{}, ...rawData];
        const worksheet = XLSX.utils.json_to_sheet(worksheetData);
        worksheetData.forEach((row) => {
            Object.keys(row).forEach((key, index) => {
                const value = row[key] ? row[key].toString() : "";
                const headerLen = key.length;
                const valueLen = value.length;
                objectMaxLength[index] = Math.max(
                    objectMaxLength[index] || headerLen,
                    valueLen
                );
            });
        });

        worksheet['!cols'] = objectMaxLength.map(width => ({
            width: width + 2
        }));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "BuildDetails");

        XLSX.writeFile(workbook, "Precision_Report.xlsx");
    };

    useEffect(() => {
        const searchData = data?.tempData?.filter((member) =>
            member?.user_display_name?.toLowerCase()?.includes(search?.toLowerCase())
        );
        setData((prev) => ({ ...prev, rowData: searchData }))
    }, [search, data?.tempData]);

    return { columns, data, validateReport, selectedRow, precisionDetailsExportToExcel, confirmationPopup, setConfirmationPopup, search, setSearch, loader, listRef, showInfo, isHide, popupFilter, setPopupFilter, getPrecisionReport }
}